// ============================================================================
// Tab "News & Competitor" della scheda cliente.
//
// È l'anagrafica delle fonti che alimentano la sezione "News & Competitor"
// della dashboard che condividiamo col cliente: i profili social da tenere
// d'occhio, le testate da cui arrivano le news e gli argomenti da monitorare.
// Per Alessandra Tassoni le fonti sono già caricate (sono quelle usate finora);
// per gli altri clienti il form parte vuoto e si compila a mano.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Users, Newspaper, Tag, Plus, Pencil, Trash2, ExternalLink, Loader2, X, Check, Eye, EyeOff, Search } from 'lucide-react';

export type TipoFonte = 'competitor' | 'news' | 'argomento';

export interface FonteIntel {
  id: string;
  client_id: string;
  tipo: TipoFonte;
  nome: string;
  url: string | null;
  handle: string | null;
  piattaforma: string | null;
  follower: number | null;
  note: string | null;
  attivo: boolean;
}

interface Props {
  clienteId: string;
  clienteNome: string;
  /** true = sola lettura (ruoli senza permessi di modifica) */
  readOnly?: boolean;
}

const PIATTAFORME = ['instagram', 'tiktok', 'youtube', 'facebook', 'linkedin'];

const token = () => localStorage.getItem('bymarke_token');

const fmtFollower = (n: number | null): string => {
  if (n === null || n === undefined) return '';
  return n >= 1000 ? `${Math.round(n / 1000)}K follower` : `${n} follower`;
};

// Bozza del form: gli stessi campi per tutti e tre i tipi, con quelli non
// pertinenti semplicemente non mostrati.
interface Bozza {
  nome: string;
  handle: string;
  piattaforma: string;
  follower: string;
  url: string;
  note: string;
}

const bozzaVuota: Bozza = { nome: '', handle: '', piattaforma: 'instagram', follower: '', url: '', note: '' };

const bozzaDa = (f: FonteIntel): Bozza => ({
  nome: f.nome,
  handle: f.handle || '',
  piattaforma: f.piattaforma || 'instagram',
  follower: f.follower !== null && f.follower !== undefined ? String(f.follower) : '',
  url: f.url || '',
  note: f.note || '',
});

const inputClass =
  'w-full px-3 py-2.5 sm:py-2 border border-slate-200 dark:border-gray-600 rounded-xl text-slate-900 dark:text-slate-100 bg-white dark:bg-gray-700 focus:border-[#fdf751] focus:ring-2 focus:ring-[#fdf751]/20 transition-all text-base sm:text-sm';

// ============================================================================
// Form inline (aggiunta e modifica usano lo stesso markup).
// Sta fuori dal componente padre di proposito: definito dentro, verrebbe
// rimontato a ogni tasto e l'input perderebbe il focus.
// ============================================================================
const FormFonte: React.FC<{
  tipo: TipoFonte;
  bozza: Bozza;
  onChange: (b: Bozza) => void;
  onAnnulla: () => void;
  onSalva: () => void;
  salvando: boolean;
}> = ({ tipo, bozza, onChange, onAnnulla, onSalva, salvando }) => (
  <div className="rounded-xl border border-slate-200 dark:border-gray-600 p-3 sm:p-4 bg-slate-50 dark:bg-gray-700/40 space-y-3">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <div className={tipo === 'argomento' ? 'sm:col-span-2' : ''}>
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          {tipo === 'competitor' ? 'Nome del competitor' : tipo === 'news' ? 'Nome della fonte' : 'Argomento'}
        </label>
        <input
          type="text"
          autoFocus
          value={bozza.nome}
          onChange={(e) => onChange({ ...bozza, nome: e.target.value })}
          placeholder={
            tipo === 'competitor' ? 'es. Massimo Imparato'
              : tipo === 'news' ? 'es. Idealista — Aste immobiliari'
              : 'es. aste immobiliari'
          }
          className={inputClass}
        />
      </div>

      {tipo === 'competitor' && (
        <>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Profilo (@handle)</label>
            <input
              type="text"
              value={bozza.handle}
              onChange={(e) => onChange({ ...bozza, handle: e.target.value })}
              placeholder="@nomeprofilo"
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Piattaforma</label>
            <select
              value={bozza.piattaforma}
              onChange={(e) => onChange({ ...bozza, piattaforma: e.target.value })}
              className={inputClass}
            >
              {PIATTAFORME.map((p) => (
                <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Follower (facoltativo)</label>
            <input
              type="number"
              min={0}
              value={bozza.follower}
              onChange={(e) => onChange({ ...bozza, follower: e.target.value })}
              placeholder="es. 25000"
              className={inputClass}
            />
          </div>
        </>
      )}

      {tipo !== 'argomento' && (
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
            Link {tipo === 'news' ? 'al sito o al feed' : 'al profilo'}
            {tipo === 'competitor' && (
              <span className="font-normal text-slate-400 dark:text-slate-500"> — se lo lasci vuoto lo ricavo dall'handle</span>
            )}
          </label>
          <input
            type="url"
            value={bozza.url}
            onChange={(e) => onChange({ ...bozza, url: e.target.value })}
            placeholder={tipo === 'news' ? 'https://www.idealista.it/news/aste-immobiliari/' : 'https://www.instagram.com/...'}
            className={inputClass}
          />
        </div>
      )}

      <div className="sm:col-span-2">
        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Note (facoltative)</label>
        <input
          type="text"
          value={bozza.note}
          onChange={(e) => onChange({ ...bozza, note: e.target.value })}
          placeholder="Perché lo seguiamo / cosa cercare"
          className={inputClass}
        />
      </div>
    </div>

    <div className="flex justify-end gap-2">
      <button
        type="button"
        onClick={onAnnulla}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-gray-600 transition-colors"
      >
        <X size={14} /> Annulla
      </button>
      <button
        type="button"
        disabled={salvando}
        onClick={onSalva}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:opacity-90 transition-opacity disabled:opacity-50"
      >
        {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Salva
      </button>
    </div>
  </div>
);

// ============================================================================
// Una delle tre sezioni: competitor / fonti news / argomenti
// ============================================================================
interface SezioneProps {
  tipo: TipoFonte;
  titolo: string;
  descrizione: string;
  icona: React.ReactNode;
  etichettaAggiungi: string;
  vuoto: string;
  elenco: FonteIntel[];
  readOnly: boolean;
  formAperto: string | null;
  bozza: Bozza;
  salvando: boolean;
  onBozza: (b: Bozza) => void;
  onNuova: (tipo: TipoFonte) => void;
  onModifica: (f: FonteIntel) => void;
  onAnnulla: () => void;
  onSalva: (tipo: TipoFonte) => void;
  onElimina: (f: FonteIntel) => void;
  onCambiaAttivo: (f: FonteIntel) => void;
}

const SezioneFonti: React.FC<SezioneProps> = ({
  tipo, titolo, descrizione, icona, etichettaAggiungi, vuoto, elenco, readOnly,
  formAperto, bozza, salvando, onBozza, onNuova, onModifica, onAnnulla, onSalva, onElimina, onCambiaAttivo,
}) => {
  const form = (
    <FormFonte
      tipo={tipo}
      bozza={bozza}
      onChange={onBozza}
      onAnnulla={onAnnulla}
      onSalva={() => onSalva(tipo)}
      salvando={salvando}
    />
  );

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {icona}
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100" style={{ letterSpacing: '-0.02em' }}>
            {titolo}
          </h2>
          <span className="text-xs font-semibold text-slate-400 dark:text-slate-500">{elenco.length}</span>
        </div>
        {!readOnly && formAperto !== `new:${tipo}` && (
          <button
            type="button"
            onClick={() => onNuova(tipo)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-semibold border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:border-slate-400 dark:hover:border-gray-500 transition-colors flex-shrink-0"
          >
            <Plus size={14} /> {etichettaAggiungi}
          </button>
        )}
      </div>
      <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mb-4">{descrizione}</p>

      {formAperto === `new:${tipo}` && <div className="mb-4">{form}</div>}

      {elenco.length === 0 && formAperto !== `new:${tipo}` ? (
        <p className="text-sm text-slate-400 dark:text-slate-500 py-4">{vuoto}</p>
      ) : tipo === 'argomento' ? (
        /* Gli argomenti sono parole chiave: stanno bene come chip */
        <div className="flex flex-wrap gap-2">
          {elenco.map((f) => (
            <span
              key={f.id}
              className={`inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-full text-sm border ${
                f.attivo
                  ? 'border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200'
                  : 'border-dashed border-slate-200 dark:border-gray-600 text-slate-400 dark:text-slate-500 line-through'
              }`}
              title={f.note || undefined}
            >
              {f.nome}
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => onElimina(f)}
                  className="p-1 rounded-full text-slate-400 hover:text-red-500 transition-colors"
                  title="Elimina argomento"
                >
                  <X size={13} />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-gray-700">
          {elenco.map((f) => (
            <div key={f.id} className="py-3 first:pt-0">
              {formAperto === f.id ? form : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className={`text-sm font-semibold ${f.attivo ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'}`}>
                        {f.nome}
                      </span>
                      {f.handle && <span className="text-xs text-slate-500 dark:text-slate-400">{f.handle}</span>}
                      {f.piattaforma && f.piattaforma !== 'instagram' && (
                        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-slate-400">
                          {f.piattaforma}
                        </span>
                      )}
                      {f.follower ? <span className="text-xs text-slate-400 dark:text-slate-500">{fmtFollower(f.follower)}</span> : null}
                      {!f.attivo && (
                        <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-700 text-slate-500 dark:text-slate-400">
                          sospesa
                        </span>
                      )}
                    </div>
                    {f.note && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{f.note}</p>}
                    {f.url && (
                      <a
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mt-0.5 break-all"
                      >
                        <ExternalLink size={11} className="flex-shrink-0" /> {f.url.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                  {!readOnly && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => onCambiaAttivo(f)}
                        className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
                        title={f.attivo ? 'Sospendi il monitoraggio' : 'Riattiva il monitoraggio'}
                      >
                        {f.attivo ? <Eye size={15} /> : <EyeOff size={15} />}
                      </button>
                      <button
                        type="button"
                        onClick={() => onModifica(f)}
                        className="p-2 rounded-lg text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
                        title="Modifica"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onElimina(f)}
                        className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Elimina"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Pagina
// ============================================================================
const ClientIntelSources: React.FC<Props> = ({ clienteId, clienteNome, readOnly = false }) => {
  const [fonti, setFonti] = useState<FonteIntel[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  // Form aperto: 'new:<tipo>' per l'aggiunta, l'id della fonte per la modifica
  const [formAperto, setFormAperto] = useState<string | null>(null);
  const [bozza, setBozza] = useState<Bozza>(bozzaVuota);
  const [salvando, setSalvando] = useState(false);
  // Ricerca news lanciata a mano (il giro automatico è ogni mattina alle 06:30)
  const [cercando, setCercando] = useState(false);
  const [esitoRicerca, setEsitoRicerca] = useState<string | null>(null);

  const carica = useCallback(async () => {
    setLoading(true);
    setErrore(null);
    try {
      const res = await fetch(`/api/intel-sources?client_id=${clienteId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setErrore(json.message || 'Non riesco a caricare le fonti.');
        setFonti([]);
      } else {
        setFonti(json.data.fonti || []);
      }
    } catch {
      setErrore('Errore di rete nel caricamento delle fonti.');
      setFonti([]);
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => { carica(); }, [carica]);

  const apriNuova = (tipo: TipoFonte) => {
    setBozza(bozzaVuota);
    setFormAperto(`new:${tipo}`);
  };

  const apriModifica = (f: FonteIntel) => {
    setBozza(bozzaDa(f));
    setFormAperto(f.id);
  };

  const chiudiForm = () => {
    setFormAperto(null);
    setBozza(bozzaVuota);
  };

  // Per i competitor il link si ricava dall'handle: chi compila scrive solo
  // @nome e non deve incollare anche l'URL.
  const urlDaBozza = (tipo: TipoFonte): string => {
    if (bozza.url.trim()) return bozza.url.trim();
    if (tipo !== 'competitor') return '';
    const h = bozza.handle.trim().replace(/^@+/, '');
    if (!h) return '';
    if (bozza.piattaforma === 'instagram') return `https://www.instagram.com/${h}/`;
    if (bozza.piattaforma === 'tiktok') return `https://www.tiktok.com/@${h}`;
    if (bozza.piattaforma === 'youtube') return `https://www.youtube.com/@${h}`;
    return '';
  };

  const salva = async (tipo: TipoFonte) => {
    const nome = bozza.nome.trim();
    if (!nome) {
      alert(tipo === 'argomento' ? 'Scrivi l\'argomento da monitorare' : 'Il nome è obbligatorio');
      return;
    }
    if (tipo === 'news' && !bozza.url.trim()) {
      alert('Per una fonte news serve il link al sito o al feed');
      return;
    }
    setSalvando(true);
    const inModifica = !!formAperto && !formAperto.startsWith('new:');
    const payload: Record<string, unknown> = {
      nome,
      url: urlDaBozza(tipo) || null,
      note: bozza.note.trim() || null,
    };
    if (tipo === 'competitor') {
      payload.handle = bozza.handle.trim() || null;
      payload.piattaforma = bozza.piattaforma;
      payload.follower = bozza.follower.trim() ? parseInt(bozza.follower, 10) : null;
    }
    try {
      const res = await fetch(
        inModifica ? `/api/intel-sources/${formAperto}` : '/api/intel-sources',
        {
          method: inModifica ? 'PUT' : 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
          body: JSON.stringify(inModifica ? payload : { ...payload, client_id: clienteId, tipo }),
        }
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        alert(json.message || 'Salvataggio non riuscito, riprova.');
        return;
      }
      const salvata: FonteIntel = json.data;
      setFonti((prev) => (inModifica ? prev.map((f) => (f.id === salvata.id ? salvata : f)) : [...prev, salvata]));
      chiudiForm();
      // Il backend fa partire da solo la raccolta un minuto dopo l'ultima
      // modifica: lo diciamo, così nessuno aspetta il giro del mattino.
      if (!inModifica) {
        setEsitoRicerca('Fonte salvata: fra circa un minuto leggiamo da soli news e competitor, i risultati compaiono nella dashboard del cliente e nel tab News & Competitor del Copywriter entro qualche minuto.');
      }
    } catch {
      alert('Errore di rete: riprova tra poco.');
    } finally {
      setSalvando(false);
    }
  };

  const elimina = async (f: FonteIntel) => {
    if (!window.confirm(`Eliminare "${f.nome}" dalle fonti di ${clienteNome}?`)) return;
    try {
      const res = await fetch(`/api/intel-sources/${f.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        alert(json.message || 'Eliminazione non riuscita.');
        return;
      }
      setFonti((prev) => prev.filter((x) => x.id !== f.id));
      if (formAperto === f.id) chiudiForm();
    } catch {
      alert('Errore di rete: riprova tra poco.');
    }
  };

  // Sospende una fonte senza cancellarla: resta censita ma non va monitorata
  const cambiaAttivo = async (f: FonteIntel) => {
    const attivo = !f.attivo;
    setFonti((prev) => prev.map((x) => (x.id === f.id ? { ...x, attivo } : x)));
    try {
      const res = await fetch(`/api/intel-sources/${f.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ attivo }),
      });
      if (!res.ok) throw new Error('update fallito');
    } catch {
      setFonti((prev) => prev.map((x) => (x.id === f.id ? { ...x, attivo: !attivo } : x)));
      alert('Non sono riuscito ad aggiornare la fonte.');
    }
  };

  // Cerca le news adesso invece di aspettare il giro delle 06:30. Ci mette un
  // paio di minuti: è una ricerca vera sul web, non una lettura dal database.
  const cercaNews = async () => {
    setCercando(true);
    setEsitoRicerca(null);
    try {
      const res = await fetch(`/api/intel-sources/client/${clienteId}/raccogli`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token()}` },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) {
        setEsitoRicerca(json.message || 'La ricerca non è riuscita, riprova tra poco.');
        return;
      }
      const { nuove, saltate, competitor } = json.data;
      const news =
        nuove > 0
          ? `${nuove} ${nuove === 1 ? 'nuova segnalazione' : 'nuove segnalazioni'} nella dashboard del cliente${saltate ? ` (${saltate} già presenti)` : ''}.`
          : saltate > 0
            ? 'Nessuna news nuova: le notizie trovate erano già segnalate.'
            : 'Nessuna notizia recente trovata sugli argomenti di questo cliente.';
      // Competitor: quanti profili letti da Instagram, quanti reel nuovi e
      // trascritti, e quali account l'API non riesce a leggere (privati o non
      // business/creator: lì non c'è niente da fare, va cambiato profilo).
      let comp = '';
      if (competitor && competitor.competitor > 0) {
        comp = ` Competitor: ${competitor.competitor} ${competitor.competitor === 1 ? 'profilo letto' : 'profili letti'}, ${competitor.nuovi} reel nuovi, ${competitor.trascritti} trascritti.`;
        if (competitor.nonLeggibili?.length) {
          comp += ` Non leggibili (account privato o non business): ${competitor.nonLeggibili.join(', ')}.`;
        }
      }
      setEsitoRicerca(news + comp);
    } catch {
      setEsitoRicerca('Errore di rete: riprova tra poco.');
    } finally {
      setCercando(false);
    }
  };

  // La sezione lato cliente non si accende a mano: c'è finché c'è almeno una
  // fonte attiva (stessa regola applicata dal backend sulla pagina del cliente).
  const sezioneAttiva = fonti.some((f) => f.attivo);

  if (loading) {
    return (
      <div className="py-12 text-center text-slate-500 dark:text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        Carico le fonti…
      </div>
    );
  }

  // Props condivise da tutte e tre le sezioni
  const comuni = {
    readOnly,
    formAperto,
    bozza,
    salvando,
    onBozza: setBozza,
    onNuova: apriNuova,
    onModifica: apriModifica,
    onAnnulla: chiudiForm,
    onSalva: salva,
    onElimina: elimina,
    onCambiaAttivo: cambiaAttivo,
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {errore && (
        <div className="rounded-xl border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/30 px-4 py-3 text-sm text-red-700 dark:text-red-300">
          {errore}
        </div>
      )}

      {/* Stato della sezione lato cliente: non si accende a mano, segue le fonti */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-slate-200 dark:border-gray-700 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            {sezioneAttiva
              ? <Check size={16} className="mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
              : <EyeOff size={16} className="mt-0.5 flex-shrink-0 text-slate-400" />}
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {sezioneAttiva
                  ? `La sezione "News & Competitor" è visibile nella dashboard di ${clienteNome}`
                  : `La sezione "News & Competitor" non compare nella dashboard di ${clienteNome}`}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-500">
                {sezioneAttiva
                  ? 'Appena aggiungi una fonte partiamo da soli (un minuto dopo l\'ultima modifica) e poi ogni mattina alle 6:30 rileggiamo notizie e reel dei competitor per la sua dashboard.'
                  : 'Basta aggiungere una fonte qui sotto — competitor, testata o argomento — e la sezione compare da sé.'}
              </p>
            </div>
          </div>
          {!readOnly && sezioneAttiva && (
            <button
              type="button"
              onClick={cercaNews}
              disabled={cercando}
              title="Cerca subito le notizie e legge i reel dei competitor, senza aspettare il giro del mattino"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold border border-slate-200 dark:border-gray-600 text-slate-700 dark:text-slate-200 hover:border-slate-400 dark:hover:border-gray-500 transition-colors disabled:opacity-60 flex-shrink-0"
            >
              {cercando ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              {cercando ? 'Sto aggiornando…' : 'Aggiorna news e competitor'}
            </button>
          )}
        </div>
        {cercando && (
          <p className="text-xs text-slate-400 dark:text-slate-500 mt-3 sm:text-right">
            È una ricerca vera sul web più la lettura dei reel dei competitor: ci vogliono alcuni minuti, puoi lasciare la pagina aperta.
          </p>
        )}
        {esitoRicerca && !cercando && (
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 sm:text-right">{esitoRicerca}</p>
        )}
      </div>

      <SezioneFonti
        {...comuni}
        tipo="competitor"
        titolo="Competitor da monitorare"
        descrizione="I profili social che teniamo d'occhio per questo cliente: da qui arrivano i contenuti virali e gli spunti da riproporre."
        icona={<Users size={18} className="text-slate-400" />}
        etichettaAggiungi="Aggiungi competitor"
        vuoto="Nessun competitor censito. Aggiungi i profili social da tenere d'occhio."
        elenco={fonti.filter((f) => f.tipo === 'competitor')}
      />

      <SezioneFonti
        {...comuni}
        tipo="news"
        titolo="Fonti news"
        descrizione="Testate, portali e feed da cui prendiamo le notizie di settore su cui costruire i contenuti."
        icona={<Newspaper size={18} className="text-slate-400" />}
        etichettaAggiungi="Aggiungi fonte"
        vuoto="Nessuna fonte news censita. Aggiungi i siti da cui vuoi ricevere le notizie."
        elenco={fonti.filter((f) => f.tipo === 'news')}
      />

      <SezioneFonti
        {...comuni}
        tipo="argomento"
        titolo="Argomenti da monitorare"
        descrizione="Le parole chiave che rendono una notizia rilevante per questo cliente."
        icona={<Tag size={18} className="text-slate-400" />}
        etichettaAggiungi="Aggiungi argomento"
        vuoto="Nessun argomento censito."
        elenco={fonti.filter((f) => f.tipo === 'argomento')}
      />
    </div>
  );
};

export default ClientIntelSources;
